import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'configured/axios'
import OptionGroup from './OptionGroup'

import NextComparisonLink from 'components/NextComparisonLink'
import PairComparisonLink from 'components/PairComparisonLink'
import TrelloLinkPanel from 'components/TrelloLinkPanel'

const List = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [list, setList] = useState({ name: null, options: [] })
  const [editingName, setEditingName] = useState(false)
  const [saving, setSaving] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [confirmingArchive, setConfirmingArchive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [newOptionName, setNewOptionName] = useState('')
  const [selectedOptions, setSelectedOptions] = useState([])
  const [editingOptionId, setEditingOptionId] = useState(null)

  const isArchived = Boolean(list.archived_at)

  const updateListName = (newName) => {
    setList(prevList => ({ ...prevList, name: newName }))
  }

  const onClickOption = (optionId) => {
    if(selectedOptions.includes(optionId)) {
      setSelectedOptions( prev => prev.filter( x => x != optionId ) )
    } else {
      setSelectedOptions( prev => [...prev, optionId].slice(-2) )
    }
  }

  const handleEditOption = (optionId) => setEditingOptionId(optionId)
  const handleSaveOption = (optionId, newName) => {
    setSaving(true)
    axios.put(`/api/lists/${id}/options/${optionId}`, { option: { name: newName } })
      .then(response => {
        // Reload the entire list to get fresh option_groups
        return axios.get(`/api/lists/${id}`)
      })
      .then(response => {
        setList(response.data)
        setEditingOptionId(null)
        setSaving(false)
      })
      .catch(error => {
        console.error('Error saving option:', error)
        setSaving(false)
        setEditingOptionId(null)
      })
  }

  const saveList = () => {
    const previousName = list.name
    if (!list.name || list.name.trim() === '') {
      // Reload so blank edit does not stick as the visible title
      axios.get(`/api/lists/${id}`)
        .then(response => {
          setList(response.data)
          setEditingName(false)
        })
        .catch(() => {
          setEditingName(false)
        })
      return
    }

    setSaving(true)

    axios.put(`/api/lists/${id}`, { list: { name: list.name } })
      .then(response => {
        const savedList = response.data
        // Keep existing options/option_groups on screen; update response is flat list attrs
        setList(prevList => ({
          ...prevList,
          ...savedList,
          options: prevList.options,
          option_groups: prevList.option_groups
        }))
        setEditingName(false)
        setSaving(false)
      })
      .catch(error => {
        console.error('Error saving list:', error)
        // Failed save must not look successful: restore previous name, keep options
        setList(prevList => ({ ...prevList, name: previousName }))
        setSaving(false)
        setEditingName(false)
      })
  }

  const saveNewOption = (e) => {
    e.preventDefault()
    if (!newOptionName || newOptionName.trim() === '') {
      return
    }

    axios.post(`/api/lists/${id}/options`, { option: { name: newOptionName } })
      .then(response => {
        return axios.get(`/api/lists/${id}`)
      })
      .then(response => {
        setList(response.data)
        setNewOptionName('')
      })
      .catch(error => {
        console.error('Error saving option:', error)
      })
  }

  const requestArchive = () => {
    if (archiving) return
    setConfirmingArchive(true)
  }

  const cancelArchive = () => {
    if (archiving) return
    setConfirmingArchive(false)
  }

  const confirmArchive = () => {
    if (archiving) return
    setArchiving(true)
    axios.put(`/api/lists/${id}`, { list: { archived_at: new Date().toISOString() } })
      .then(() => {
        navigate('/lists')
      })
      .catch(err => {
        console.error('Error archiving list:', err)
        setError('Failed to archive the list.')
        setArchiving(false)
        setConfirmingArchive(false)
      })
  }

  const unarchiveList = () => {
    if (archiving) return
    setArchiving(true)
    axios.put(`/api/lists/${id}`, { list: { archived_at: null } })
      .then(response => {
        const savedList = response.data
        setList(prevList => ({
          ...prevList,
          ...savedList,
          options: prevList.options,
          option_groups: prevList.option_groups
        }))
        setArchiving(false)
      })
      .catch(err => {
        console.error('Error unarchiving list:', err)
        setError('Failed to unarchive the list.')
        setArchiving(false)
      })
  }

  useEffect(() => {
    axios.get(`/api/lists/${id}`)
      .then(response => {
        setList(response.data)
        setLoading(false)
      })
      .catch(err => {
        setError('Failed to load the list.')
        setLoading(false)
      })
  }, [id])

  if (loading) return <p>Loading...</p>
  if (error) return <div className="alert alert-danger">{error}</div>

  return (
    <>
      {editingName ? (
        <input
          type="text"
          value={list.name || ''}
          onChange={(e) => updateListName(e.target.value)}
          onBlur={() => saveList()}
          onKeyDown={(e) => { if (e.key === 'Enter') { saveList() } }}
          className="form-control form-control-lg"
          placeholder="Enter list name..."
          autoFocus
        />
      ) : (
        <h1
          onClick={() => setEditingName(true)}
          tabIndex={0}
          role="button"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setEditingName(true);
          }}
        >
          {list.name || 'Click to add name'}
        </h1>
      )}

      {isArchived ? (
        <div className="d-flex align-items-center gap-3 mb-3">
          <span className="text-muted">Archived</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={unarchiveList}
            disabled={archiving}
          >
            {archiving ? 'Working…' : 'Unarchive'}
          </button>
        </div>
      ) : confirmingArchive ? (
        <div className="alert alert-secondary d-flex flex-wrap align-items-center gap-2 mb-3" role="status">
          <span>Archive this list? It will move to Archived until you unarchive it.</span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={confirmArchive}
            disabled={archiving}
          >
            {archiving ? 'Archiving…' : 'Confirm archive'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={cancelArchive}
            disabled={archiving}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={requestArchive}
            disabled={archiving}
          >
            Archive
          </button>
        </div>
      )}

      <TrelloLinkPanel
        listId={id}
        list={list}
        onLinked={(linkedList) => setList(linkedList)}
      />

      <form onSubmit={saveNewOption} className="mt-4">
        <div className="input-group">
          <input
            type="text"
            value={newOptionName}
            onChange={(e) => setNewOptionName(e.target.value)}
            className="form-control"
            placeholder="Add an option ..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveNewOption(e)
              }
            }}
          />
        </div>
      </form>

      <ul className='list-inline mt-3'>
        {list.options && list.options.length >= 2 && (
          <>
            <li className="list-inline-item">
              <NextComparisonLink listId={id} setError={setError} />
            </li>

            <li className="list-inline-item">
              <Link to={`/lists/${id}/graph`}>
                View as Graph
              </Link>
            </li>
          </>
        )}

        { selectedOptions.length == 2 && (
          <li className="list-inline-item">
            <PairComparisonLink
              listId={id}
              setError={setError}
              optionIds={selectedOptions}
            />
          </li>
        ) }
      </ul>


      <div className="mt-4">
        {
          list.option_groups && list.option_groups.length > 0 ?
            (
              list.option_groups.map( og => (
                <OptionGroup key={`optionGroup-${og.id}`}
                  optionGroup={og}
                  onClickOption={onClickOption}
                  selectedOptions={selectedOptions}
                  editingOptionId={editingOptionId}
                  onEditOption={handleEditOption}
                  onSaveOption={handleSaveOption}
                />
              ) )
            ) :
            ( <p className="text-muted">
              No options yet. Add your first option above!
            </p> )
        }
      </div>
    </>
  )
}

export default List
